import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  File,
  FileAudio,
  FileVideo,
  Folder,
  HardDrive,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  RefreshCw,
  Search,
  Trash2,
  Download,
  X,
} from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './CompanyStorageBrowser.css';

const API_BASE_URL = getApiBaseUrl();
const LIST_PAGE_SIZE = 100;
const VIRTUAL_THRESHOLD = 40;
const ROW_ESTIMATE_PX = 36;

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
}

async function fetchBrowser(path, { cursor, limit = LIST_PAGE_SIZE } = {}) {
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_BASE_URL}/api/config/company/storage/browser${qs}`, {
    headers: authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json.data;
}

async function fetchPreviewUrl(path) {
  const res = await fetch(`${API_BASE_URL}/api/config/company/storage/browser/preview-url`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({ path }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json.data;
}

function mergeBrowserItems(prev, incoming) {
  const map = new Map();
  (Array.isArray(prev) ? prev : []).forEach((it) => {
    if (it?.path) map.set(it.path, it);
  });
  (Array.isArray(incoming) ? incoming : []).forEach((it) => {
    if (it?.path) map.set(it.path, it);
  });
  return [...map.values()];
}

async function deleteBrowserFiles(paths) {
  const res = await fetch(`${API_BASE_URL}/api/config/company/storage/browser/delete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ paths }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json.data;
}

async function fetchDownloadUrls(paths) {
  const res = await fetch(`${API_BASE_URL}/api/config/company/storage/browser/download-urls`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({ paths }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json.data;
}

async function downloadOpaqueFile(url, fileName) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || `Falha ao baixar (${res.status})`);
  }
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = fileName || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

function formatSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n.toLocaleString('pt-BR')} B`;
  if (n < 1024 * 1024) {
    const kb = n / 1024;
    return `${kb.toLocaleString('pt-BR', { maximumFractionDigits: kb >= 100 ? 0 : 1 })} KB`;
  }
  const mb = n / (1024 * 1024);
  return `${mb.toLocaleString('pt-BR', { maximumFractionDigits: mb >= 100 ? 0 : 1 })} MB`;
}

function resolveMediaKind(item) {
  if (item?.mediaKind) return item.mediaKind;
  const ext = String(item?.name || '')
    .split('.')
    .pop()
    .toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'image';
  if (ext === 'webm') return 'audio';
  if (['avi', 'mp4', 'mkv', 'mov'].includes(ext)) return 'video';
  return null;
}

function rectsIntersect(a, b) {
  return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

function compareItems(a, b, key, dir) {
  const mul = dir === 'desc' ? -1 : 1;
  if (key === 'size') {
    const sa = a.type === 'file' ? Number(a.size) || 0 : -1;
    const sb = b.type === 'file' ? Number(b.size) || 0 : -1;
    if (sa !== sb) return (sa - sb) * mul;
  } else if (key === 'date') {
    const ta = a.type === 'file' && a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.type === 'file' && b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    if (ta !== tb) return (ta - tb) * mul;
  }
  return (
    String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', {
      sensitivity: 'base',
      numeric: true,
    }) * mul
  );
}

function RowIcon({ item }) {
  if (item.type === 'dir') return <Folder size={16} strokeWidth={1.75} />;
  const kind = resolveMediaKind(item);
  if (kind === 'image') return <ImageIcon size={16} strokeWidth={1.75} />;
  if (kind === 'audio') return <FileAudio size={16} strokeWidth={1.75} />;
  if (kind === 'video') return <FileVideo size={16} strokeWidth={1.75} />;
  return <File size={16} strokeWidth={1.75} />;
}

function SortTh({ label, colKey, sort, onSort, className }) {
  const active = sort.key === colKey;
  return (
    <button
      type="button"
      className={'csb-th csb-th-btn ' + (className || '') + (active ? ' is-sorted' : '')}
      role="columnheader"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(colKey)}
    >
      <span>{label}</span>
      <span className="csb-th-sort" aria-hidden>
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUp size={12} strokeWidth={2.25} />
          ) : (
            <ArrowDown size={12} strokeWidth={2.25} />
          )
        ) : (
          <ArrowUp size={12} strokeWidth={2.25} className="csb-th-sort-idle" />
        )}
      </span>
    </button>
  );
}

/**
 * Overlay gerenciador: verve_gestor/{clientId}/customers|go
 * Layout tipo Explorer (Nome / Data / Tamanho) + pesquisa + seleção.
 */
export default function CompanyStorageBrowser({ open, onClose }) {
  const [path, setPath] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [anchorPath, setAnchorPath] = useState(null);
  const [marquee, setMarquee] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const bodyRef = useRef(null);
  const tableScrollRef = useRef(null);
  const rowRefs = useRef(new Map());
  const marqueeRef = useRef(null);
  const skipClickRef = useRef(false);
  const rowsRef = useRef([]);
  const nextCursorRef = useRef(null);
  const loadMoreLockRef = useRef(false);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setAnchorPath(null);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewExpanded(false);
    setPreviewLoading(false);
    setPreview(null);
  }, []);

  const load = useCallback(
    async (nextPath = '', { append = false, cursor = null } = {}) => {
      if (append) {
        if (loadMoreLockRef.current) return;
        loadMoreLockRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError('');
        setPreview(null);
        setPreviewExpanded(false);
        setPreviewLoading(false);
        setQuery('');
        clearSelection();
        setConfirmDelete(false);
        setHasMore(false);
        nextCursorRef.current = null;
      }
      try {
        const data = await fetchBrowser(nextPath, {
          cursor: append ? cursor : null,
          limit: LIST_PAGE_SIZE,
        });
        const incoming = Array.isArray(data.items) ? data.items : [];
        setPath(data.path || '');
        setBreadcrumbs(Array.isArray(data.breadcrumbs) ? data.breadcrumbs : []);
        setItems((prev) => (append ? mergeBrowserItems(prev, incoming) : incoming));
        const more = Boolean(data.hasMore && data.nextCursor);
        setHasMore(more);
        nextCursorRef.current = more ? data.nextCursor : null;
      } catch (e) {
        setError(e.message || 'Falha ao carregar arquivos.');
        if (!append) setItems([]);
        setHasMore(false);
        nextCursorRef.current = null;
      } finally {
        if (append) {
          loadMoreLockRef.current = false;
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [clearSelection]
  );

  const loadMore = useCallback(() => {
    if (!hasMore || !nextCursorRef.current || loading || loadingMore) return;
    load(path, { append: true, cursor: nextCursorRef.current });
  }, [hasMore, load, loading, loadingMore, path]);

  useEffect(() => {
    if (!open) return undefined;
    setPath('');
    setQuery('');
    clearSelection();
    load('');
    return undefined;
  }, [open, load, clearSelection]);

  useEffect(() => {
    const el = tableScrollRef.current || bodyRef.current;
    if (!open || !el || !hasMore) return undefined;
    const onScroll = () => {
      if (loading || loadingMore || loadMoreLockRef.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 140) {
        loadMore();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [open, hasMore, loadMore, loading, loadingMore, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmDelete) {
        e.preventDefault();
        e.stopPropagation();
        if (!deleting) setConfirmDelete(false);
        return;
      }
      if (preview) {
        e.preventDefault();
        e.stopPropagation();
        closePreview();
        return;
      }
      if (selected.size) {
        e.preventDefault();
        e.stopPropagation();
        clearSelection();
        return;
      }
      onClose?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [
    open,
    preview,
    closePreview,
    onClose,
    confirmDelete,
    deleting,
    selected.size,
    clearSelection,
  ]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = Array.isArray(items) ? items : [];
    if (!q) return list;
    return list.filter((it) => String(it.name || '').toLowerCase().includes(q));
  }, [items, query]);

  const rows = useMemo(() => {
    const dirs = filtered.filter((i) => i.type === 'dir');
    const files = filtered.filter((i) => i.type === 'file');
    const by = (a, b) => compareItems(a, b, sort.key, sort.dir);
    dirs.sort(by);
    files.sort(by);
    return [...dirs, ...files];
  }, [filtered, sort]);

  rowsRef.current = rows;

  const useVirtual = rows.length >= VIRTUAL_THRESHOLD;
  const rowVirtualizer = useVirtualizer({
    count: useVirtual ? rows.length : 0,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => ROW_ESTIMATE_PX,
    overscan: 12,
  });

  const toggleSort = useCallback((key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  }, []);

  const selectedFiles = useMemo(() => {
    return rows.filter((r) => r.type === 'file' && selected.has(r.path));
  }, [rows, selected]);

  const applyRangeSelection = useCallback(
    (endPath, additive) => {
      const endIdx = rows.findIndex((r) => r.path === endPath);
      if (endIdx < 0) return;
      const startIdx = Math.max(
        0,
        rows.findIndex((r) => r.path === (anchorPath || endPath))
      );
      const from = Math.min(startIdx, endIdx);
      const to = Math.max(startIdx, endIdx);
      const rangePaths = [];
      for (let i = from; i <= to; i += 1) {
        if (rows[i].type === 'file') rangePaths.push(rows[i].path);
      }
      setSelected((prev) => {
        const next = additive ? new Set(prev) : new Set();
        rangePaths.forEach((p) => next.add(p));
        return next;
      });
    },
    [rows, anchorPath]
  );

  const openPreview = useCallback(async (item) => {
    if (item.type !== 'file' || !item.previewable) return;
    setPreviewExpanded(false);
    setPreviewLoading(true);
    setPreview({
      name: item.name,
      url: null,
      mediaKind: resolveMediaKind(item) || 'image',
    });
    try {
      const data = await fetchPreviewUrl(item.path);
      setPreview({
        name: data?.name || item.name,
        url: data?.viewUrl || null,
        mediaKind: data?.mediaKind || resolveMediaKind(item) || 'image',
      });
      if (!data?.viewUrl) {
        setError('Não foi possível abrir a pré-visualização.');
        setPreview(null);
      }
    } catch (e) {
      setError(e.message || 'Falha ao abrir pré-visualização.');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleRowClick = (e, item) => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    const multi = e.ctrlKey || e.metaKey;
    const range = e.shiftKey;

    if (item.type === 'dir') {
      if (multi || range) return;
      load(item.path);
      return;
    }

    if (range) {
      e.preventDefault();
      applyRangeSelection(item.path, multi);
      return;
    }

    if (multi) {
      e.preventDefault();
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(item.path)) next.delete(item.path);
        else next.add(item.path);
        return next;
      });
      setAnchorPath(item.path);
      return;
    }

    setSelected(new Set([item.path]));
    setAnchorPath(item.path);
  };

  const handleRowDoubleClick = (e, item) => {
    e.preventDefault();
    if (item.type === 'dir') {
      load(item.path);
      return;
    }
    openPreview(item);
  };

  const onBodyMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest?.('.csb-table-row')) return;
    if (e.target.closest?.('.csb-table-head')) return;
    if (loading || preview || confirmDelete) return;

    const body = bodyRef.current;
    if (!body) return;
    const br = body.getBoundingClientRect();
    const start = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: body.scrollLeft,
      scrollTop: body.scrollTop,
    };
    const additive = e.ctrlKey || e.metaKey;
    const base = additive ? new Set(selected) : new Set();
    if (!additive) clearSelection();

    marqueeRef.current = { start, base, additive };
    setMarquee({
      left: e.clientX - br.left + body.scrollLeft,
      top: e.clientY - br.top + body.scrollTop,
      width: 0,
      height: 0,
    });

    const onMove = (ev) => {
      const st = marqueeRef.current;
      if (!st) return;
      const bodyEl = bodyRef.current;
      if (!bodyEl) return;
      const rect = bodyEl.getBoundingClientRect();
      const curX = ev.clientX - rect.left + bodyEl.scrollLeft;
      const curY = ev.clientY - rect.top + bodyEl.scrollTop;
      const ox = st.start.x - rect.left + st.start.scrollLeft;
      const oy = st.start.y - rect.top + st.start.scrollTop;
      const box = {
        left: Math.min(ox, curX),
        top: Math.min(oy, curY),
        width: Math.abs(curX - ox),
        height: Math.abs(curY - oy),
      };
      setMarquee(box);

      if (box.width < 4 && box.height < 4) return;

      const absBox = {
        left: rect.left - bodyEl.scrollLeft + box.left,
        top: rect.top - bodyEl.scrollTop + box.top,
        right: rect.left - bodyEl.scrollLeft + box.left + box.width,
        bottom: rect.top - bodyEl.scrollTop + box.top + box.height,
      };

      const hit = new Set(st.base);
      const currentRows = rowsRef.current;
      rowRefs.current.forEach((el, pathKey) => {
        if (!el) return;
        const item = currentRows.find((r) => r.path === pathKey);
        if (!item || item.type !== 'file') return;
        const rr = el.getBoundingClientRect();
        if (
          rectsIntersect(absBox, {
            left: rr.left,
            top: rr.top,
            right: rr.right,
            bottom: rr.bottom,
          })
        ) {
          hit.add(pathKey);
        }
      });
      setSelected(hit);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      marqueeRef.current = null;
      setMarquee(null);
      skipClickRef.current = true;
      window.setTimeout(() => {
        skipClickRef.current = false;
      }, 0);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const requestDelete = () => {
    if (!selectedFiles.length) return;
    setConfirmDelete(true);
  };

  const downloadSelected = async () => {
    if (!selectedFiles.length || downloading || deleting) return;
    setDownloading(true);
    setError('');
    try {
      const data = await fetchDownloadUrls(selectedFiles.map((f) => f.path));
      const files = Array.isArray(data?.files) ? data.files : [];
      if (!files.length) throw new Error('Nenhum arquivo disponível para download.');
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        await downloadOpaqueFile(file.url, file.name || selectedFiles[i]?.name || 'download');
        if (i < files.length - 1) {
          await new Promise((r) => window.setTimeout(r, 180));
        }
      }
    } catch (e) {
      setError(e.message || 'Falha ao baixar arquivos.');
    } finally {
      setDownloading(false);
    }
  };

  const confirmDeleteAction = async () => {
    if (!selectedFiles.length || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await deleteBrowserFiles(selectedFiles.map((f) => f.path));
      setConfirmDelete(false);
      clearSelection();
      await load(path);
    } catch (e) {
      setError(e.message || 'Falha ao excluir arquivos.');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  const showTable = rows.length > 0;
  const showEmpty = !loading && !error && !rows.length;
  const showBootLoading = loading && !items.length && !error;

  return createPortal(
    <div className="csb-overlay" role="presentation" onClick={onClose}>
      <div
        className="csb-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Gerenciador de arquivos"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="csb-header">
          <div className="csb-header-title">
            <HardDrive size={18} strokeWidth={2} aria-hidden />
            <div>
              <div className="csb-title">Gerenciador de arquivos</div>
              <div className="csb-subtitle">Customers e App Go do seu ambiente</div>
            </div>
          </div>
          <div className="csb-header-actions">
            {selectedFiles.length > 0 ? (
              <>
                <button
                  type="button"
                  className="csb-btn-chip csb-btn-download-chip"
                  onClick={downloadSelected}
                  disabled={loading || deleting || downloading}
                >
                  {downloading ? (
                    <Loader2 size={13} strokeWidth={1.75} className="csb-spin" aria-hidden />
                  ) : (
                    <Download size={13} strokeWidth={1.75} aria-hidden />
                  )}
                  <span>
                    {downloading ? 'Baixando…' : `Baixar (${selectedFiles.length})`}
                  </span>
                </button>
                <button
                  type="button"
                  className="csb-btn-danger csb-btn-danger-chip"
                  onClick={requestDelete}
                  disabled={loading || deleting || downloading}
                >
                  <Trash2 size={13} strokeWidth={1.75} aria-hidden />
                  <span>Excluir ({selectedFiles.length})</span>
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="csb-icon-btn"
              aria-label="Atualizar"
              title="Atualizar"
              disabled={loading}
              onClick={() => load(path)}
            >
              <RefreshCw size={16} strokeWidth={2} className={loading ? 'csb-spin' : undefined} />
            </button>
            <button type="button" className="csb-icon-btn" aria-label="Fechar" onClick={onClose}>
              <X size={18} strokeWidth={2} />
            </button>
          </div>
        </header>

        <div className="csb-toolbar">
          <nav className="csb-crumbs" aria-label="Caminho">
            {breadcrumbs.map((c, i) => {
              const last = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={`${c.path}-${i}`}>
                  {i > 0 ? (
                    <ChevronRight size={14} strokeWidth={2} className="csb-crumb-sep" aria-hidden />
                  ) : null}
                  {last ? (
                    <span className="csb-crumb is-current">{c.label}</span>
                  ) : (
                    <button
                      type="button"
                      className="csb-crumb"
                      onClick={() => load(c.path || '')}
                    >
                      {c.label}
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
          <label className="csb-search">
            <Search size={14} strokeWidth={2} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                clearSelection();
              }}
              placeholder="Pesquisar nesta pasta…"
              aria-label="Pesquisar nesta pasta"
              autoComplete="off"
            />
            {query ? (
              <button
                type="button"
                className="csb-search-clear"
                aria-label="Limpar pesquisa"
                onClick={() => {
                  setQuery('');
                  clearSelection();
                }}
              >
                <X size={12} strokeWidth={2.25} />
              </button>
            ) : null}
          </label>
        </div>

        <div
          className="csb-body"
          ref={bodyRef}
          onMouseDown={onBodyMouseDown}
        >
          {error ? <div className="csb-error" role="alert">{error}</div> : null}

          {showBootLoading ? (
            <div className="csb-empty">
              <Loader2 size={22} className="csb-spin" aria-hidden />
              <span>Carregando…</span>
            </div>
          ) : null}

          {showEmpty ? (
            <div className="csb-empty">
              <Folder size={28} strokeWidth={1.5} aria-hidden />
              <span>{query.trim() ? 'Nenhum resultado na pesquisa' : 'Pasta vazia'}</span>
            </div>
          ) : null}

          {showTable ? (
            <div className="csb-table-wrap" role="table" aria-label="Arquivos e pastas">
              <div className="csb-table-head" role="row">
                <SortTh
                  label="Nome"
                  colKey="name"
                  sort={sort}
                  onSort={toggleSort}
                  className="csb-col-name"
                />
                <SortTh
                  label="Data de modificação"
                  colKey="date"
                  sort={sort}
                  onSort={toggleSort}
                  className="csb-col-date"
                />
                <SortTh
                  label="Tamanho"
                  colKey="size"
                  sort={sort}
                  onSort={toggleSort}
                  className="csb-col-size"
                />
              </div>
              <div className="csb-table-scroll" ref={tableScrollRef}>
                <div
                  className="csb-table-body"
                  role="rowgroup"
                  style={
                    useVirtual
                      ? {
                          height: `${rowVirtualizer.getTotalSize()}px`,
                          position: 'relative',
                          width: '100%',
                        }
                      : undefined
                  }
                >
                  {(useVirtual
                    ? rowVirtualizer.getVirtualItems()
                    : rows.map((_, index) => ({ index }))
                  ).map((virtualRow) => {
                    const index = virtualRow.index;
                    const item = rows[index];
                    if (!item) return null;
                    const isSelected = selected.has(item.path);
                    const clickable = item.type === 'dir' || Boolean(item.previewable);
                    const rowStyle = useVirtual
                      ? {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }
                      : undefined;
                    return (
                      <button
                        key={item.path}
                        type="button"
                        role="row"
                        aria-selected={isSelected}
                        data-index={index}
                        ref={(el) => {
                          if (el) {
                            rowRefs.current.set(item.path, el);
                            if (useVirtual) rowVirtualizer.measureElement(el);
                          } else {
                            rowRefs.current.delete(item.path);
                          }
                        }}
                        style={rowStyle}
                        className={
                          'csb-table-row' +
                          (item.type === 'dir' ? ' is-dir' : '') +
                          (resolveMediaKind(item) === 'audio' ? ' is-audio' : '') +
                          (resolveMediaKind(item) === 'video' ? ' is-video' : '') +
                          (clickable ? ' is-clickable' : '') +
                          (isSelected ? ' is-selected' : '')
                        }
                        onClick={(e) => handleRowClick(e, item)}
                        onDoubleClick={(e) => handleRowDoubleClick(e, item)}
                        title={item.name}
                      >
                        <span className="csb-td csb-col-name" role="cell">
                          <span className="csb-row-icon" aria-hidden>
                            <RowIcon item={item} />
                          </span>
                          <span className="csb-row-name">{item.name}</span>
                        </span>
                        <span className="csb-td csb-col-date" role="cell">
                          {item.type === 'file' ? formatWhen(item.updatedAt) : '—'}
                        </span>
                        <span className="csb-td csb-col-size" role="cell">
                          {item.type === 'file' ? formatSize(item.size) : '—'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {hasMore || loadingMore ? (
                  <div className="csb-load-more">
                    {loadingMore ? (
                      <>
                        <Loader2 size={14} className="csb-spin" aria-hidden />
                        <span>Carregando mais…</span>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="csb-btn-ghost csb-load-more-btn"
                        onClick={loadMore}
                      >
                        Carregar mais
                      </button>
                    )}
                  </div>
                ) : null}
                {query.trim() && hasMore ? (
                  <div className="csb-search-hint">
                    Pesquisa nos itens já carregados — role para carregar mais.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {marquee && (marquee.width > 3 || marquee.height > 3) ? (
            <div
              className="csb-marquee"
              style={{
                left: marquee.left,
                top: marquee.top,
                width: marquee.width,
                height: marquee.height,
              }}
              aria-hidden
            />
          ) : null}

          {loading && items.length > 0 ? (
            <div className="csb-loading-veil" role="status" aria-live="polite">
              <div className="csb-loading-card">
                <Loader2 size={26} className="csb-spin" aria-hidden />
                <span>Abrindo pasta…</span>
              </div>
            </div>
          ) : null}
        </div>

        {confirmDelete ? (
          <div
            className="csb-confirm"
            role="dialog"
            aria-label="Confirmar exclusão"
            onClick={() => {
              if (!deleting) setConfirmDelete(false);
            }}
          >
            <div className="csb-confirm-panel" onClick={(e) => e.stopPropagation()}>
              <div className="csb-confirm-title">Excluir arquivos?</div>
              <p className="csb-confirm-text">
                {selectedFiles.length === 1
                  ? `O arquivo “${selectedFiles[0].name}” será excluído permanentemente.`
                  : `${selectedFiles.length} arquivos serão excluídos permanentemente.`}
              </p>
              {selectedFiles.length > 1 && selectedFiles.length <= 8 ? (
                <ul className="csb-confirm-list">
                  {selectedFiles.map((f) => (
                    <li key={f.path}>{f.name}</li>
                  ))}
                </ul>
              ) : null}
              <div className="csb-confirm-actions">
                <button
                  type="button"
                  className="csb-btn-ghost"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="csb-btn-confirm"
                  disabled={deleting}
                  onClick={confirmDeleteAction}
                >
                  {deleting ? (
                    <>
                      <Loader2 size={14} className="csb-spin" aria-hidden />
                      Excluindo…
                    </>
                  ) : (
                    'Excluir'
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {preview ? (
          <div
            className="csb-preview"
            role="dialog"
            aria-label={`Pré-visualizar ${preview.name}`}
            onClick={closePreview}
          >
            {previewExpanded &&
            preview.url &&
            (preview.mediaKind === 'image' || !preview.mediaKind) ? (
              <div
                className="csb-preview-fullscreen"
                onClick={(e) => {
                  e.stopPropagation();
                  closePreview();
                }}
              >
                <button
                  type="button"
                  className="csb-preview-fullscreen-close"
                  aria-label="Fechar imagem expandida"
                  onClick={(e) => {
                    e.stopPropagation();
                    closePreview();
                  }}
                >
                  <X size={20} strokeWidth={2.25} />
                </button>
                <div
                  className="csb-preview-fullscreen-stage"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={preview.url}
                    alt={preview.name}
                    className="csb-preview-fullscreen-img"
                    draggable={false}
                  />
                </div>
              </div>
            ) : (
              <div
                className={
                  'csb-preview-panel' +
                  (preview.mediaKind === 'audio' ? ' csb-preview-panel--audio' : '') +
                  (preview.mediaKind === 'video' ? ' csb-preview-panel--video' : '')
                }
                onClick={(e) => e.stopPropagation()}
              >
                <div className="csb-preview-head">
                  <span className="csb-preview-name">{preview.name}</span>
                  <div className="csb-preview-head-actions">
                    {preview.url && (preview.mediaKind === 'image' || !preview.mediaKind) ? (
                      <button
                        type="button"
                        className="csb-icon-btn"
                        aria-label="Expandir imagem"
                        title="Expandir"
                        onClick={() => setPreviewExpanded(true)}
                      >
                        <Maximize2 size={16} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="csb-icon-btn"
                      aria-label="Fechar pré-visualização"
                      onClick={closePreview}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <div className="csb-preview-body">
                  {previewLoading || !preview.url ? (
                    <div className="csb-preview-loading">
                      <Loader2 size={22} className="csb-spin" aria-hidden />
                      <span>Abrindo pré-visualização…</span>
                    </div>
                  ) : null}
                  {!previewLoading && preview.url && preview.mediaKind === 'audio' ? (
                    <div className="csb-preview-audio">
                      <FileAudio size={36} strokeWidth={1.5} aria-hidden />
                      <audio
                        key={preview.url}
                        src={preview.url}
                        controls
                        autoPlay
                        preload="metadata"
                        className="csb-preview-player"
                      >
                        Seu navegador não suporta a reprodução deste áudio.
                      </audio>
                    </div>
                  ) : null}
                  {!previewLoading && preview.url && preview.mediaKind === 'video' ? (
                    <video
                      key={preview.url}
                      src={preview.url}
                      controls
                      autoPlay
                      preload="metadata"
                      className="csb-preview-video"
                    >
                      Seu navegador não suporta a reprodução deste vídeo.
                    </video>
                  ) : null}
                  {!previewLoading &&
                  preview.url &&
                  (preview.mediaKind === 'image' || !preview.mediaKind) ? (
                    <button
                      type="button"
                      className="csb-preview-img-btn"
                      aria-label="Expandir imagem"
                      onClick={() => setPreviewExpanded(true)}
                    >
                      <img
                        src={preview.url}
                        alt={preview.name}
                        className="csb-preview-img"
                        draggable={false}
                      />
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
